import * as _ from 'lodash-es';
import { ComputedRef, onBeforeUnmount, onMounted, ref, shallowRef } from 'vue';
import { appMessage } from '@repo/telemetry-config/messages';

export interface DxMessageHooks {
  onSend?: (message: unknown) => void;
  /**
   * Emit Angular-bridge telemetry (`app.message.*` via @repo/telemetry-config) from inside the
   * composable: records outbound sends. Opt-in, so generic postMessage usages (demos, PDF
   * viewer) don't emit bridge metrics.
   */
  track?: boolean;
  /**
   * Attach the inbound 'message' listener. Default true. Set false for send-only instances
   * (e.g. useDxState) so they don't hold a redundant listener — the single receiver
   * (AppMessage) owns inbound handling.
   */
  listen?: boolean;
}

export const useDxMessage = (
  target: ComputedRef<Window | null | undefined>,
  targetOrigin: string,
  hooks?: DxMessageHooks
) => {
  // data
  const event = ref<MessageEvent>();
  const data = ref<any>();
  const error = shallowRef<MessageEvent | null>(null);

  const track = hooks?.track ?? false;
  const listen = hooks?.listen ?? true;

  // events
  const onMessage = (e: MessageEvent) => {
    if (e.origin !== targetOrigin) return;
    event.value = e;
    data.value = e.data;
  };
  const onMessageError = (e: MessageEvent) => {
    error.value = e;
  };

  // methods
  const sendMessage = (data: any) => {
    if (target.value && targetOrigin) {
      const message = _.cloneDeep(data);
      target.value.postMessage(message, targetOrigin);
      if (track) appMessage.recordOutbound(message?.event, appMessage.toContext(message ?? {}, 'outbound'));
      hooks?.onSend?.(message);
    }
  };

  // hooks
  onMounted(() => {
    if (!listen) return;
    window.addEventListener('message', onMessage);
    window.addEventListener('messageerror', onMessageError);
  });
  onBeforeUnmount(() => {
    if (!listen) return;
    window.removeEventListener('message', onMessage);
    window.removeEventListener('messageerror', onMessageError);
  });

  return {
    event,
    data,
    error,
    sendMessage
  };
};
